import { LinkCustomField } from '../services/api';
import { User, Mail, Phone, Building, MessageSquare } from 'lucide-react';

interface CustomFormFieldsProps {
    fields: LinkCustomField[];
    values: Record<string, any>;
    onChange: (fieldName: string, value: any) => void;
    errors?: Record<string, string>;
    className?: string;
}

export function CustomFormFields({ fields, values, onChange, errors, className = '' }: CustomFormFieldsProps) {
    if (!fields || fields.length === 0) {
        return null;
    }

    const getIcon = (fieldType: string, fieldName: string) => {
        // Map common field names to icons
        const lowerName = fieldName.toLowerCase();
        if (lowerName.includes('phone')) return Phone;
        if (lowerName.includes('company') || lowerName.includes('organization')) return Building;
        if (lowerName.includes('email')) return Mail;
        if (lowerName.includes('name')) return User;
        
        // Default by type
        switch (fieldType) {
            case 'email': return Mail;
            case 'phone': return Phone;
            case 'textarea': return MessageSquare;
            default: return null;
        }
    };

    const renderField = (field: LinkCustomField) => {
        const Icon = getIcon(field.field_type, field.field_name);
        const value = values[field.field_name] ?? '';
        const error = errors?.[field.field_name];

        switch (field.field_type) {
            case 'textarea':
                return (
                    <div key={field.id} className={`form-group ${className}`}>
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {Icon && <Icon size={14} />}
                            {field.field_label} {field.is_required && '*'}
                        </label>
                        <textarea
                            className={`form-textarea ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => onChange(field.field_name, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.is_required}
                            rows={3}
                        />
                        {error && <span className="form-error">{error}</span>}
                    </div>
                );

            case 'select':
                return (
                    <div key={field.id} className={`form-group ${className}`}>
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {Icon && <Icon size={14} />}
                            {field.field_label} {field.is_required && '*'}
                        </label>
                        <select
                            className={`form-select ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => onChange(field.field_name, e.target.value)}
                            required={field.is_required}
                        >
                            <option value="">{field.placeholder || 'Select an option'}</option>
                            {field.options?.map((option, idx) => (
                                <option key={idx} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                        {error && <span className="form-error">{error}</span>}
                    </div>
                );

            case 'checkbox':
                return (
                    <div key={field.id} className={`form-group ${className}`}>
                        <label className="form-label" style={{ marginBottom: 'var(--space-2)' }}>
                            {field.field_label} {field.is_required && '*'}
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {field.options?.map((option, idx) => (
                                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={Array.isArray(value) && value.includes(option)}
                                        onChange={(e) => {
                                            const currentValues = Array.isArray(value) ? value : [];
                                            if (e.target.checked) {
                                                onChange(field.field_name, [...currentValues, option]);
                                            } else {
                                                onChange(field.field_name, currentValues.filter(v => v !== option));
                                            }
                                        }}
                                    />
                                    <span>{option}</span>
                                </label>
                            ))}
                        </div>
                        {error && <span className="form-error">{error}</span>}
                    </div>
                );

            case 'email':
            case 'phone':
            case 'text':
            default:
                return (
                    <div key={field.id} className={`form-group ${className}`}>
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {Icon && <Icon size={14} />}
                            {field.field_label} {field.is_required && '*'}
                        </label>
                        <input
                            type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
                            className={`form-input ${error ? 'error' : ''}`}
                            value={value}
                            onChange={(e) => onChange(field.field_name, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.is_required}
                        />
                        {error && <span className="form-error">{error}</span>}
                    </div>
                );
        }
    };

    return (
        <>
            {fields.sort((a, b) => a.display_order - b.display_order).map(field => renderField(field))}
        </>
    );
}

// AAM-style variant for embed mode
interface AAMCustomFormFieldsProps {
    fields: LinkCustomField[];
    values: Record<string, any>;
    onChange: (fieldName: string, value: any) => void;
    errors?: Record<string, string>;
    hideLabels?: boolean; // Hide field labels
}

export function AAMCustomFormFields({ fields, values, onChange, errors, hideLabels = false }: AAMCustomFormFieldsProps) {
    if (!fields || fields.length === 0) {
        return null;
    }

    const renderField = (field: LinkCustomField) => {
        const value = values[field.field_name] ?? '';
        const error = errors?.[field.field_name];

        switch (field.field_type) {
            case 'textarea':
                return (
                    <div key={field.id} className="aam-form-group">
                        {!hideLabels && (
                            <label className="aam-label">
                                {field.field_label} {field.is_required && '*'}
                            </label>
                        )}
                        <div className="aam-input-wrapper textarea">
                            <MessageSquare size={18} />
                            <textarea
                                value={value}
                                onChange={(e) => onChange(field.field_name, e.target.value)}
                                placeholder={field.placeholder || ''}
                                required={field.is_required}
                                rows={3}
                                className="aam-textarea"
                            />
                        </div>
                        {error && <span className="aam-error">{error}</span>}
                    </div>
                );

            case 'select':
                return (
                    <div key={field.id} className="aam-form-group">
                        {!hideLabels && (
                            <label className="aam-label">
                                {field.field_label} {field.is_required && '*'}
                            </label>
                        )}
                        <div className="aam-input-wrapper">
                            <select
                                value={value}
                                onChange={(e) => onChange(field.field_name, e.target.value)}
                                required={field.is_required}
                                className="aam-input"
                            >
                                <option value="">{field.placeholder || 'Select an option'}</option>
                                {field.options?.map((option, idx) => (
                                    <option key={idx} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {error && <span className="aam-error">{error}</span>}
                    </div>
                );

            case 'checkbox':
                return (
                    <div key={field.id} className="aam-form-group">
                        {!hideLabels && (
                            <label className="aam-label" style={{ marginBottom: '8px' }}>
                                {field.field_label} {field.is_required && '*'}
                            </label>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '4px' }}>
                            {field.options?.map((option, idx) => (
                                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={Array.isArray(value) && value.includes(option)}
                                        onChange={(e) => {
                                            const currentValues = Array.isArray(value) ? value : [];
                                            if (e.target.checked) {
                                                onChange(field.field_name, [...currentValues, option]);
                                            } else {
                                                onChange(field.field_name, currentValues.filter(v => v !== option));
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <span>{option}</span>
                                </label>
                            ))}
                        </div>
                        {error && <span className="aam-error">{error}</span>}
                    </div>
                );

            case 'email':
            case 'phone':
            case 'text':
            default:
                const getInputIcon = () => {
                    const lowerName = field.field_name.toLowerCase();
                    if (lowerName.includes('phone')) return Phone;
                    if (lowerName.includes('company') || lowerName.includes('organization')) return Building;
                    if (field.field_type === 'email') return Mail;
                    if (lowerName.includes('name')) return User;
                    return User; // Default
                };

                const InputIcon = getInputIcon();

                return (
                    <div key={field.id} className="aam-form-group">
                        {!hideLabels && (
                            <label className="aam-label">
                                {field.field_label} {field.is_required && '*'}
                            </label>
                        )}
                        <div className="aam-input-wrapper">
                            <InputIcon size={18} />
                            <input
                                type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
                                value={value}
                                onChange={(e) => onChange(field.field_name, e.target.value)}
                                placeholder={field.placeholder || ''}
                                required={field.is_required}
                                className="aam-input"
                            />
                        </div>
                        {error && <span className="aam-error">{error}</span>}
                    </div>
                );
        }
    };

    return (
        <>
            {fields.sort((a, b) => a.display_order - b.display_order).map(field => renderField(field))}
        </>
    );
}
